import React, { useRef, useState } from 'react';
import { parseLocaleNumber, formatFixed, isValidNumericInput } from '../utils/numberFormatting';

export interface NumericInputProps {
  value: string;
  onChange: (value: string) => void;
  decimals?: number;
  placeholder?: string;
  onBlurNumber?: (num: number | null) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  suffix?: string;
  min?: number;
  max?: number;
  clearOnFocus?: boolean;
}

export default function NumericInput({
  value,
  onChange,
  decimals = 2,
  placeholder = '0.00',
  onBlurNumber,
  disabled = false,
  style,
  className,
  suffix,
  min,
  max,
  clearOnFocus = true,
}: NumericInputProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previousValue, setPreviousValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    if (clearOnFocus) {
      setPreviousValue(value);
      onChange('');
    }
    setIsFocused(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (isValidNumericInput(newValue)) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);

    let valueToFormat = value;
    if (clearOnFocus && value === '' && previousValue) {
      valueToFormat = previousValue;
    }

    const num = parseLocaleNumber(valueToFormat);

    let finalNum = num;
    if (finalNum !== null) {
      if (min !== undefined && finalNum < min) finalNum = min;
      if (max !== undefined && finalNum > max) finalNum = max;
    }

    const formatted = formatFixed(finalNum, decimals);
    onChange(formatted);

    if (onBlurNumber) {
      onBlurNumber(finalNum);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      if (clearOnFocus && previousValue) {
        onChange(previousValue);
      }
      inputRef.current?.blur();
    }
  };

  const displayPlaceholder = isFocused && clearOnFocus && previousValue
    ? previousValue
    : placeholder;

  const baseStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    paddingRight: suffix ? '40px' : '12px',
    border: '1px solid #e0e3ef',
    borderRadius: '6px',
    fontSize: '14px',
    textAlign: 'right',
    fontFamily: 'monospace',
    background: disabled ? '#f5f5f5' : 'white',
    cursor: disabled ? 'not-allowed' : 'text',
    ...style,
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={displayPlaceholder}
        disabled={disabled}
        className={className}
        style={baseStyle}
      />
      {suffix && (
        <span
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888',
            fontSize: '13px',
            pointerEvents: 'none',
          }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

export function useNumericField(initialValue: string = '') {
  const [value, setValue] = React.useState(initialValue);
  const [numericValue, setNumericValue] = React.useState<number | null>(
    parseLocaleNumber(initialValue)
  );

  const handleChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleBlurNumber = (num: number | null) => {
    setNumericValue(num);
  };

  const reset = (newValue: string = '') => {
    setValue(newValue);
    setNumericValue(parseLocaleNumber(newValue));
  };

  return {
    value,
    numericValue,
    onChange: handleChange,
    onBlurNumber: handleBlurNumber,
    reset,
    props: {
      value,
      onChange: handleChange,
      onBlurNumber: handleBlurNumber,
    },
  };
}
